//TODO:  Do we need substate for this Task Object State???   Split into SubState like Contract?

import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import * as moment from 'moment';
import { TaskObjectDefaults } from '@Core/models/proprietary/task/task-object.defaults';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    AddTaskToQueue,
    CloneTask,
    NewTask,
    NotifyDocumentSaved,
    PrepareRequest,
    RemoveTaskFromQueue,
    ResetState,
    ResetTask,
    SetCurrentReference,
    SetCurrentReferenceContract,
    SetCurrentTaskHeaderLayout,
    SetProjectState,
    SetTaskObjectState,
    SetTaskReference,
    SuppressTaskHeader,
    UpdateProject,
    UpdateTask,
    UpdateTaskHeader
} from './task-object.actions';
import { TaskHeaderLayoutType, TaskHeaderType } from './task-object.types';

export interface TaskReferenceInfo {
    Entity_ID?: string;
    Entity_Name?: string;
    Role_Type?: string;
    Contract_ID?: string;
    Contract_Num?: string;
    Claim_ID?: string;
    Book_Claim_Num?: string;
}

export interface TaskObjectStateModel {
    currentTaskID: string; //move to substate?
    currentProjectID: string;
    currentReference: TaskReferenceInfo;
    entities: any;
    task: any;
    project: any;
    suppressTaskHeader: boolean;
    taskHeader: TaskHeaderType;
    taskQueue: any;
    documentUpdateTimestamp: number;
}

const TaskObjectStateDefaults: TaskObjectStateModel = {
    currentTaskID: null,
    currentProjectID: null,
    currentReference: {},
    entities: {},
    task: {},
    project: {},
    suppressTaskHeader: true,
    taskHeader: {
        Default_Layout: TaskHeaderLayoutType.Normal,
        Current_Layout: TaskHeaderLayoutType.Normal,
        Task_Desc_Lines: '0'
    },
    taskQueue: {},
    documentUpdateTimestamp: 0
};

@State<TaskObjectStateModel>({
    name: 'taskObjectState',
    defaults: TaskObjectStateDefaults
    //children: [ContractState, LocationState, ProductState, ArticleState, EntityState, AddressState, PhoneState]
})
@Injectable()
export class TaskObjectState {
    constructor(private app: AppService, private helper: StateHelperService, private store: Store) { }

    @Selector()
    static entities(state: TaskObjectStateModel) {
        let tempEntities = { ...state.entities };
        tempEntities.Task = state.task;
        tempEntities.Project = state.project;
        return tempEntities;
    }

    @Selector()
    static suppressTaskHeader(state: TaskObjectStateModel) {
        return state.suppressTaskHeader;
    }

    @Selector()
    static allowTaskHeader(state: TaskObjectStateModel) {
        return !state.suppressTaskHeader;
    }

    @Selector()
    static taskHeader(state: TaskObjectStateModel) {
        return state.taskHeader;
    }

    @Selector()
    static task(state: TaskObjectStateModel) {
        return state.task[state.currentTaskID] || null;
    }

    @Selector()
    static currentInfo(state: TaskObjectStateModel) {
        let taskResolved: boolean = false;
        let taskObjectCode: string = null;
        if (state.task[state.currentTaskID]) {
            taskResolved = state.task[state.currentTaskID].Resolved_Date > '';
            taskObjectCode = state.task[state.currentTaskID].Task_Object.Object_Code;
        }
        return {
            Task_ID: state.currentTaskID == '-1' ? null : state.currentTaskID,
            Project_ID: state.currentProjectID == '-1' ? null : state.currentProjectID,
            Task_Object_Code: taskObjectCode,
            Task_Resolved: taskResolved,
            Entity_ID: state.currentReference.Entity_ID,
            Entity_Name: state.currentReference.Entity_Name,
            Role_Type: state.currentReference.Role_Type,
            Contract_ID: state.currentReference.Contract_ID,
            Contract_Num: state.currentReference.Contract_Num,
            Claim_ID: state.currentReference.Claim_ID,
            Book_Claim_Num: state.currentReference.Book_Claim_Num
        };
    }

    @Selector()
    static taskByTaskID(state: TaskObjectStateModel) {
        return (taskID: string) => {
            if (taskID) {
                for (var i in state.task) {
                    if (state.task[i].Task_ID == taskID) {
                        return state.task[i];
                    }
                }
            }
            return {};
        };
    }

    @Selector()
    static project(state: TaskObjectStateModel) {
        return state.project[state.currentProjectID] || null;
    }

    @Selector()
    static assignmentCount(state: TaskObjectStateModel) {
        let count = 0;
        for (var i in state.entities.Assignment) {
            count++;
        }
        return count;
    }

    @Selector()
    static assignment(state: TaskObjectStateModel) {
        let result = [];
        for (var i in state.entities.Assignment) {
            result.push(state.entities.Assignment[i]);
        }

        result.sort((a, b) => {
            let date1 = moment(a.Assign_Date);
            let date2 = moment(b.Assign_Date);
            if (date1.diff(date2) > 0) {
                return -1;
            }
            if (date1.diff(date2) < 0) {
                return 1;
            }
            return 0;
        });

        return result;
    }

    @Selector()
    static lastAssignment(state: TaskObjectStateModel) {
        let assignments = [];
        for (var i in state.entities.Assignment) {
            assignments.push(state.entities.Assignment[i]);
        }

        assignments.sort((a, b) => {
            let date1 = moment(a.Assign_Date);
            let date2 = moment(b.Assign_Date);
            if (date1.diff(date2) > 0) {
                return -1;
            }
            if (date1.diff(date2) < 0) {
                return 1;
            }
            return 0;
        });

        let assignment: any = null;
        for (var i in assignments) {
            assignment = assignments[i];
            break;
        }
        return assignment;
    }

    @Selector()
    static priorTask(state: TaskObjectStateModel) {
        for (var i in state.entities.PriorTask) {
            return state.entities.PriorTask[i];
        }
        return null;
    }

    @Selector()
    static nextTasks(state: TaskObjectStateModel) {
        let result = [];
        for (var i in state.entities.NextTask) {
            result.push(state.entities.NextTask[i]);
        }
        return result;
    }

    @Selector()
    static events(state: TaskObjectStateModel) {
        let result = [];
        for (var i in state.entities.Event) {
            result.push(state.entities.Event[i]);
        }
        return result;
    }

    @Selector()
    static objectEvents(state: TaskObjectStateModel) {
        let result = [];
        let taskObject: any;
        if (state.task[state.currentTaskID]) {
            taskObject = state.task[state.currentTaskID].Task_Object;
        }

        for (var i in state.entities.Event) {
            if (state.entities.Event[i].Event_Object.Object_Code == taskObject.Object_Code) {
                result.push(state.entities.Event[i].Event_Code.toUpperCase());
            }
        }

        return result;
    }

    @Selector()
    static taskQueue(state: TaskObjectStateModel) {
        let result = [];
        for (var i in state.taskQueue) {
            result.push(state.taskQueue[i]);
        }
        return result;
    }

    @Selector()
    static firstTaskInQueue(state: TaskObjectStateModel) {
        for (var i in state.taskQueue) {
            return state.taskQueue[i];
        }
        return null;
    }

    @Selector()
    static documentUpdateTimestamp(state: TaskObjectStateModel) {
        return state.documentUpdateTimestamp;
    }

    @Action(UpdateTask)
    updateTask(sc: StateContext<TaskObjectStateModel>, action: UpdateTask) {
        const state = sc.getState();
        const taskID = action.task.Task_ID;
        if (!taskID) {
            return;
        }
        const currentTask = state.task[taskID];
        let updateTask: any = { ...cloneDeep(currentTask), ...cloneDeep(action.task) };

        if (updateTask.Route === null) {
            delete updateTask.Route;
        }

        if (updateTask.Route) {
            // specical routing processing
            if (updateTask.Route.Assign_To) {
                let assignToType = 'USER';
                if (updateTask.Route.Assign_To.Assign_To_Type > '') {
                    assignToType = updateTask.Route.Assign_To.Assign_To_Type;
                }
                switch (assignToType) {
                    case 'WORKGROUP':
                        updateTask.Route.Assign_To.User_Entity_ID = '996000';
                        updateTask.Route.Assign_To.User_Entity_Name = 'Unassigned';
                        updateTask.Route.Notify_Immediate_Ind = 'N';
                        break;
                    case 'AUTOASSIST':
                        updateTask.Route.Assign_To.User_Entity_ID = '998000';
                        updateTask.Route.Assign_To.User_Entity_Name = 'Automated Assistant';
                        updateTask.Route.Assign_To.WorkGroup_ID = '';
                        updateTask.Route.Assign_To.WorkGroup_Name = '';
                        updateTask.Route.Notify_Immediate_Ind = 'N';
                        updateTask.Route.Assign_Note = '';
                        break;
                    default:
                        //user
                        if (
                            updateTask.Route.Assign_To.User_Entity_ID == '996000' ||
                            updateTask.Route.Assign_To.User_Entity_ID == '998000'
                        ) {
                            updateTask.Route.Assign_To.User_Entity_ID = '';
                            updateTask.Route.Assign_To.User_Entity_Name = '';
                        }
                        updateTask.Route.Assign_To.WorkGroup_ID = '';
                        updateTask.Route.Assign_To.WorkGroup_Name = '';
                }
            }
            if (!(updateTask.Route.Notify_Immediate_Ind == 'Y')) {
                updateTask.Route.Notify_Include_Document_Ind = 'N';
            }
        }

        if (
            action.task.Active_Date_Quick_Select &&
            action.task.Active_Date_Quick_Select != '' &&
            action.task.Active_Date_Quick_Select != currentTask.Active_Date_Quick_Select
        ) {
            let today = moment(new Date(), 'YYYY-MM-DD');
            let code: string = action.task.Active_Date_Quick_Select.slice(0, 1);
            let num: number = 0;
            if (action.task.Active_Date_Quick_Select.length > 1) {
                num = Number(action.task.Active_Date_Quick_Select.slice(1));
            }
            switch (code) {
                case 'T':
                    updateTask.Active_Date = today.format('YYYY-MM-DD');
                    break;
                case 'D':
                    updateTask.Active_Date = today.add(num, 'd').format('YYYY-MM-DD');
                    break;
                case 'W':
                    updateTask.Active_Date = today.add(num, 'w').format('YYYY-MM-DD');
                    break;
                case 'M':
                    updateTask.Active_Date = today.add(num, 'M').format('YYYY-MM-DD');
                    break;
                case 'Y':
                    updateTask.Active_Date = today.add(num, 'y').format('YYYY-MM-DD');
                    break;
            }
        } else {
            if (action.task.Active_Date && action.task.Active_Date != currentTask.Active_Date) {
                updateTask.Active_Date_Quick_Select = '';
            }
        }

        if (
            action.task.Late_Date_Quick_Select &&
            action.task.Late_Date_Quick_Select != '' &&
            action.task.Late_Date_Quick_Select != currentTask.Late_Date_Quick_Select
        ) {
            let today = moment(new Date(), 'YYYY-MM-DD');
            let code: string = action.task.Late_Date_Quick_Select.slice(0, 1);
            let num: number = 0;
            if (action.task.Late_Date_Quick_Select.length > 1) {
                num = Number(action.task.Late_Date_Quick_Select.slice(1));
            }
            switch (code) {
                case 'T':
                    updateTask.Late_Date = today.format('YYYY-MM-DD');
                    break;
                case 'D':
                    updateTask.Late_Date = today.add(num, 'd').format('YYYY-MM-DD');
                    break;
                case 'W':
                    updateTask.Late_Date = today.add(num, 'w').format('YYYY-MM-DD');
                    break;
                case 'M':
                    updateTask.Late_Date = today.add(num, 'M').format('YYYY-MM-DD');
                    break;
                case 'Y':
                    updateTask.Late_Date = today.add(num, 'y').format('YYYY-MM-DD');
                    break;
            }
        } else {
            if (action.task.Late_Date && action.task.Late_Date != currentTask.Late_Date) {
                updateTask.Late_Date_Quick_Select = '';
            }
        }

        sc.patchState({
            task: {
                ...state.task,
                [taskID]: updateTask
            }
        });

        sc.dispatch([new SetCurrentReference(updateTask)]);
    }

    @Action(SetTaskReference)
    SetTaskReference(sc: StateContext<TaskObjectStateModel>, action: SetTaskReference) {
        let clear: boolean = false;
        const state = sc.getState();
        const taskID = action.task.Task_ID;

        const emptyEntity = { Entity_ID: '', Entity_Name: '', Role_Type: '' };
        const emptyContract = { Contract_ID: '', Contract_Num: '', Eff_Date: '' };
        const emptyClaim = { Claim_ID: '', Book_Claim_Num: '', Principal_Claimant: { Entity_ID: '', Entity_Name: '' } };

        if (action.refData == null) {
            clear = true;
        }

        let updateTask: any = { ...action.task };

        if (action.refType == 'Entity') {
            if (clear) {
                updateTask.Task_Reference.Entity = emptyEntity;
            } else {
                let selectedRole;

                const roles = action.refData?.Roles?.Role;

                if (roles && roles.length > 0) {
                    for (const role of roles) {
                        if ('Selected' in role && role.Selected) {
                            selectedRole = role.Role_Type;
                            break;
                        }
                    }

                    if (!selectedRole) {
                        selectedRole = roles[0].Role_Type;
                    }
                }

                updateTask.Task_Reference.Entity.Role_Type = selectedRole ? selectedRole : '';
            }

            //Setting or Clearing the Entity field will empty out the "higher" level fields (Contract and Claim).
            updateTask.Task_Reference.Contract = emptyContract;
            updateTask.Task_Reference.Claim = emptyClaim;
        }

        //Update Contract
        if (action.refType == 'Contract') {
            if (clear) {
                //Leave lower level entity data when clearing the Contract.
                updateTask.Task_Reference.Contract = emptyContract;
            } else {
                updateTask.Task_Reference.Contract.Eff_Date = action.refData.Eff_Date;
                updateTask.Task_Reference.Entity = action.refData.Consumer;
                updateTask.Task_Reference.Entity.Role_Type = 'CN';
            }

            //Setting or Clearing the Contract field will empty out the "higher" level fields (Claim).
            updateTask.Task_Reference.Claim = emptyClaim;
        }

        if (action.refType == 'Claim') {
            if (clear) {
                updateTask.Task_Reference.Claim = emptyClaim;
            } else {
                updateTask.Task_Reference.Claim.Principal_Claimant = action.refData.Claimant;
                updateTask.Task_Reference.Contract = action.refData.Contract;
                updateTask.Task_Reference.Entity = action.refData.Consumer;
                updateTask.Task_Reference.Entity.Role_Type = 'CN';
            }
        }

        sc.patchState({
            task: {
                ...state.task,
                [taskID]: { ...state.task[taskID], ...updateTask }
            }
        });
    }

    @Action(UpdateProject)
    updateProject(sc: StateContext<TaskObjectStateModel>, action: UpdateProject) {
        const state = sc.getState();
        const projectID = action.project.Project_ID;
        if (!projectID) {
            return;
        }
        const currentProject = state.project[projectID];
        let updateProject: any = { ...action.project };

        sc.patchState({
            project: {
                ...state.project,
                [projectID]: { ...currentProject, ...updateProject }
            }
        });
    }

    @Action(SetCurrentReference)
    setCurrentReference(sc: StateContext<TaskObjectStateModel>, action: SetCurrentReference) {
        const state = sc.getState();

        let ref: TaskReferenceInfo = cloneDeep(state.currentReference);
        let task = action.task;

        let newref: TaskReferenceInfo = {};
        if (task) {
            if (task.Task_Reference) {
                let entity = task.Task_Reference.Entity;
                let contract = task.Task_Reference.Contract;
                let claim = task.Task_Reference.Claim;

                if (entity && entity.Entity_ID && entity.Entity_ID > '') {
                    newref.Entity_ID = entity.Entity_ID;
                    newref.Entity_Name = entity.Entity_Name;
                    newref.Role_Type = entity.Role_Type;
                }
                if (contract && contract.Contract_ID && contract.Contract_ID > '') {
                    newref.Contract_ID = contract.Contract_ID;
                    newref.Contract_Num = contract.Contract_Num;
                }
                if (claim && claim.Claim_ID && claim.Claim_ID > '') {
                    newref.Claim_ID = claim.Claim_ID;
                    newref.Book_Claim_Num = claim.Book_Claim_Num;
                }
            } else {
                //HACK!! This else is a temp hack to get reference info from flattened data from the legacy collection control
                if (task.Task_Ref_Entity_ID && task.Task_Ref_Entity_ID > '') {
                    newref.Entity_ID = task.Task_Ref_Entity_ID;
                    newref.Entity_Name = task.Task_Ref_Entity_Name;
                    newref.Role_Type = task.Task_Ref_Role_Type;
                }
                if (task.Task_Ref_Contract_ID && task.Task_Ref_Contract_ID > '') {
                    newref.Contract_ID = task.Task_Ref_Contract_ID;
                    newref.Contract_Num = task.Task_Ref_Contract_Num;
                }
                if (task.Task_Ref_Claim_ID && task.Task_Ref_Claim_ID > '') {
                    newref.Claim_ID = task.Task_Ref_Claim_ID;
                    newref.Book_Claim_Num = task.task_Ref_Book_Claim_Num;
                }
            }
        }

        if (!(newref.Entity_ID || newref.Contract_ID || newref.Claim_ID)) {
            //if task passed in has no ref info then KEEP current reference info (don't overwrite)
            newref = ref;
        }

        sc.patchState({
            currentReference: newref
        });
    }

    @Action(SuppressTaskHeader)
    suppressTaskHeader(sc: StateContext<TaskObjectStateModel>, action: SuppressTaskHeader) {
        let state = sc.getState();
        sc.patchState({ suppressTaskHeader: action.on });
        if (!action.on) {
            sc.dispatch(new SetCurrentTaskHeaderLayout(state.taskHeader.Default_Layout));
        }
    }

    @Action(SetCurrentTaskHeaderLayout)
    setCurrentTaskHeaderLayout(sc: StateContext<TaskObjectStateModel>, action: SetCurrentTaskHeaderLayout) {
        const state = sc.getState();
        sc.patchState({ taskHeader: { ...state.taskHeader, Current_Layout: action.taskHeaderLayout } });
    }

    @Action(UpdateTaskHeader)
    updateTaskHeader(sc: StateContext<TaskObjectStateModel>, action: UpdateTaskHeader) {
        const state = sc.getState();
        sc.patchState({ taskHeader: { ...state.taskHeader, ...action.taskHeader } });
    }

    @Action(AddTaskToQueue)
    addTaskToQueue(sc: StateContext<TaskObjectStateModel>, action: AddTaskToQueue) {
        const state = sc.getState();
        let taskID = action.task.Task_ID;
        let task = cloneDeep(action.task);
        if (taskID) {
            sc.patchState({ taskQueue: { ...state.taskQueue, [taskID]: task } });
        }
    }

    @Action(RemoveTaskFromQueue)
    removeTaskFromQueue(sc: StateContext<TaskObjectStateModel>, action: RemoveTaskFromQueue) {
        const state = sc.getState();
        let taskID = action.taskID;
        let newQueue = { ...state.taskQueue };
        if (taskID) {
            delete newQueue[taskID];
            sc.patchState({ taskQueue: newQueue });
        }
    }

    @Action(SetTaskObjectState)
    setTaskObjectState(sc: StateContext<TaskObjectStateModel>, action: SetTaskObjectState) {
        const state = sc.getState();

        //Find the first (and ONLY) contract in Contract entity object
        let taskID = null;
        let protectLate = 'N';
        for (var i in action.entities.Task) {
            taskID = i;
            if (action.entities.Task[taskID].Protect_Late_Ind == 'Y') {
                protectLate = 'Y';
            }
            break;
        }

        let projectID = null;
        for (var j in action.entities.Project) {
            projectID = j;
            break;
        }

        // copy references to raw normalized entities and then remove the entities that have thier own state
        let entities = { ...action.entities };
        let task = cloneDeep(entities.Task);
        task[taskID].Saved_Protect_Late_Ind = protectLate;

        // Make sure that entities "selector" get entities from all substates.   if a new substate is added here then we need to add to the selector
        sc.setState({
            entities: entities,
            task: task, // better to put entities that can be changed in a root state element???
            project: entities.Project,
            currentTaskID: taskID,
            currentProjectID: projectID,
            currentReference: state.currentReference,
            suppressTaskHeader: state.suppressTaskHeader,
            taskHeader: state.taskHeader,
            taskQueue: state.taskQueue,
            documentUpdateTimestamp: state.documentUpdateTimestamp
        });
        if (taskID) {
            sc.dispatch([new SetCurrentReference(task[i])]);
        }
    }

    @Action(ResetState)
    resetState(sc: StateContext<TaskObjectStateModel>, action: ResetState) {
        const state = sc.getState();

        sc.setState({
            currentTaskID: null,
            currentProjectID: null,
            currentReference: {},
            entities: {},
            task: {},
            project: {},
            suppressTaskHeader: state.suppressTaskHeader,
            taskHeader: state.taskHeader,
            taskQueue: {},
            documentUpdateTimestamp: state.documentUpdateTimestamp
        });
    }

    @Action(ResetTask)
    resetTask(sc: StateContext<TaskObjectStateModel>, action: ResetTask) {
        const state = sc.getState();

        sc.setState({
            currentTaskID: null,
            currentProjectID: state.currentProjectID,
            currentReference:
                state.currentProjectID && !['0', '-1', 0, -1].includes(state.currentProjectID) ? state.currentReference : {},
            entities: {},
            task: {},
            project: state.project,
            suppressTaskHeader: state.suppressTaskHeader,
            taskHeader: state.taskHeader,
            taskQueue: state.taskQueue,
            documentUpdateTimestamp: state.documentUpdateTimestamp
        });
    }

    @Action(SetProjectState)
    setProjectState(sc: StateContext<TaskObjectStateModel>, action: SetProjectState) {
        const state = sc.getState();

        sc.patchState({
            currentProjectID: action.project.Project_ID,
            project: { [action.project.Project_ID]: action.project }
        });
    }

    @Action(NewTask)
    newTask(sc: StateContext<TaskObjectStateModel>, action: NewTask) {
        const state = sc.getState();

        let taskID = '-1';
        let task = this.helper.createDefaultItem(TaskObjectDefaults, 'Task', action.taskDefaults);
        task.Task_ID = taskID;
        this.helper.setItemAdded(task);
        task.Active_Date = this.app.getCodedDate('TODAY', 'BEGIN');

        let projectID = '-1';
        let project: any = { Project_ID: projectID, Project_Status: 'New' };

        // if project_id pass in taskDefaults on action, then use it
        if (action.taskDefaults && action.taskDefaults.Project && action.taskDefaults.Project.Project_ID) {
            projectID = action.taskDefaults.Project.Project_ID;
            if (projectID == state.currentProjectID) {
                project = cloneDeep(state.project[state.currentProjectID]);
            }
        }

        // add task to current project if current project is open.
        if (projectID == '-1' && state.project[state.currentProjectID]) {
            if (state.project[state.currentProjectID].Project_Status != 'Closed') {
                projectID = state.currentProjectID;
                project = cloneDeep(state.project[state.currentProjectID]);
            }
        }

        if (task.Task_Object && task.Task_Object.Object_Code && task.Task_Object.Object_Code.toLowerCase() == 'task') {
            let taskRef = { ...state.currentReference };
            if (taskRef) {
                if (!task.Task_Reference) {
                    task.Task_Reference = {};
                }

                if (taskRef.Entity_ID && taskRef.Entity_Name && taskRef.Role_Type) {
                    task.Task_Reference.Entity = {
                        Entity_ID: taskRef.Entity_ID,
                        Entity_Name: taskRef.Entity_Name,
                        Role_Type: taskRef.Role_Type
                    };
                }

                if (taskRef.Contract_ID && taskRef.Contract_Num) {
                    task.Task_Reference.Contract = { Contract_ID: taskRef.Contract_ID, Contract_Num: taskRef.Contract_Num };
                }

                if (taskRef.Claim_ID && taskRef.Book_Claim_Num) {
                    task.Task_Reference.Claim = { Claim_ID: taskRef.Claim_ID, Book_Claim_Num: taskRef.Book_Claim_Num };
                }
            }
        }

        sc.setState({
            currentTaskID: taskID,
            currentProjectID: projectID,
            currentReference: state.currentReference,
            entities: {
                Root: {},
                Transaction: {},
                Request: {}
            },
            task: { [taskID]: task },
            project: { [projectID]: project },
            suppressTaskHeader: state.suppressTaskHeader,
            taskHeader: state.taskHeader,
            taskQueue: state.taskQueue,
            documentUpdateTimestamp: state.documentUpdateTimestamp
        });

        sc.dispatch([new SetCurrentReference(task)]);
    }

    @Action(CloneTask)
    cloneTask(sc: StateContext<TaskObjectStateModel>, action: CloneTask) {
        const state = sc.getState();

        //TODO: Don't allow "Notes" to be cloned

        let projectID = state.currentProjectID;
        if (state.project[state.currentProjectID]) {
            if (state.project[state.currentProjectID].Project_Status == 'Closed') {
                projectID = '-1'; //can't add to closed session
            }
        }
        let project = { Project_ID: projectID };
        if (projectID == state.currentProjectID) {
            project = { ...state.project[state.currentProjectID] };
        }

        let taskID = '-1';
        let currentTask = cloneDeep(state.task[state.currentTaskID]);
        let task = {
            Task_ID: taskID,
            Edit_Status: 'A',
            Task_Status: 'New',
            Task_Status_Desc: 'Not Saved',
            Task_Desc: currentTask.Task_Desc,
            Task_Object: currentTask.Task_Object,
            Task_Reference: currentTask.Task_Reference
            //Assigned_To??
        };

        sc.setState({
            currentTaskID: taskID,
            currentProjectID: projectID,
            currentReference: state.currentReference,
            entities: {
                Root: {},
                Transaction: {},
                Request: {}
            },
            task: { [taskID]: task },
            project: { [projectID]: project },
            suppressTaskHeader: state.suppressTaskHeader,
            taskHeader: state.taskHeader,
            taskQueue: state.taskQueue,
            documentUpdateTimestamp: state.documentUpdateTimestamp
        });
    }

    @Action(PrepareRequest)
    prepareRequest(sc: StateContext<TaskObjectStateModel>, action: PrepareRequest) {
        // prepare ConceptOneRequest
        // Note: The normalize process will create a Root, Reply, and Transaction entity with an ID of 0
        //       When preparing a process transaction a Root, Request, and Transaction entities will have an ID of 1
        //       (then we will denormalize the Root entity with ID = 1)

        const state = sc.getState();

        let taskID = state.currentTaskID;

        let processRoot = {
            '?xml': { '@version': '1.0' },
            ConceptOneRequest: '1'
        };

        let processRequest = {
            TaskProcess: '1',
            SDK_Version: this.app.dsSystemInfo.objectNode.Product_Version
        };

        let processTran = {
            View: 'Full',
            Book_Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
            Book_Loc_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID,
            Task: taskID,
            ...action.tranOptions
        };

        let current = this.store.selectSnapshot(TaskObjectState.currentInfo);
        this.helper.setTaskControlAttributes(processTran, current);

        sc.patchState({
            entities: {
                ...state.entities,
                Root: {
                    ...state.entities.Root,
                    [1]: processRoot
                },
                Request: {
                    ...state.entities.Request,
                    [1]: processRequest
                },
                Transaction: {
                    ...state.entities.Transaction,
                    [1]: processTran
                }
            }
        });
    }

    @Action(NotifyDocumentSaved)
    notifyDocumentSaved(sc: StateContext<TaskObjectStateModel>, action: NotifyDocumentSaved) {
        const state = sc.getState();

        sc.patchState({
            documentUpdateTimestamp: state.documentUpdateTimestamp + 1
        });
    }

    @Action(SetCurrentReferenceContract)
    setCurrentReferenceContract(sc: StateContext<TaskObjectStateModel>, action: SetCurrentReferenceContract) {
        const state = sc.getState();
        let updateCurrentRef = { ...state.currentReference };
        let contract = action.contract;

        updateCurrentRef.Contract_ID = contract.Contract_ID;
        updateCurrentRef.Contract_Num = contract.Contract_Num;
        if (contract.Consumer) {
            updateCurrentRef.Entity_ID = contract.Consumer.Entity_ID;
            updateCurrentRef.Entity_Name = contract.Consumer.Entity_Name;
            updateCurrentRef.Role_Type = 'CN';
        }

        sc.patchState({
            currentReference: updateCurrentRef
        });
    }
}
